import { yupResolver } from '@hookform/resolvers/yup';
import {
  fetchPostIdCheck,
  fetchPostOnfidoStart,
  GetStateByStateKeyResponse,
  GetStateResponse,
} from '@iwoca/lapi-client/edge';
import { Button } from '@iwoca/orion';
import { cloneDeep, set } from 'lodash-es';
import { FormProvider, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import entrust_logo from './entrust.png';
import { TIDCheckResolutionMethod } from './IDCheck';
import { CustomerPhone, CustomerStatus } from './IDCheckNotComplete';
import { ID_CHECK_PENDING_MODAL } from './IDCheckPendingModal';
import idverse_logo from './idverse.png';
import { phoneNumberValidator } from './PhoneNumberValidator';
import {
  useGetStateByStateKey,
  usePutState,
} from '../../../../api/lending/lapiHooks';
import { HookFormInput } from '../../../../components/Input/Input';
import { useStateKey } from '../../../../hooks/useStateKey.hook';
import { WhiteLabelOverride } from '../../../components/WhiteLabelOverride/WhiteLabelOverride';
import { useModal } from '../../../store/ModalProvider';
import { useCurrentOfferId } from '../../hooks/useCurrentOfferId';

export const PhoneNumberForm = ({
  applicantUID,
  applicantStatus,
  onError,
  resolution_method,
}: {
  applicantUID: string | undefined;
  applicantStatus: CustomerStatus;
  onError: () => void;
  resolution_method: TIDCheckResolutionMethod;
}) => {
  const { stateKey } = useStateKey();
  const { state, refetchState } = useGetStateByStateKey();
  const { putState } = usePutState();
  const { openModal } = useModal(ID_CHECK_PENDING_MODAL);
  const currentOfferId = useCurrentOfferId();

  if (!state) {
    throw new Error('No state was provided');
  }
  const primaryPhone = getCustomerPrimaryPhone(state, applicantUID);

  const sendSmsButtonText =
    applicantStatus === 'error' ? 'Try again' : 'Send text';

  const addPhoneNumberToStateObject = (
    phoneNumber: string,
    customerUID: string | undefined,
    state: GetStateResponse['data'] | null,
  ) => {
    const newState = cloneDeep(state);

    if (!newState || !newState.application?.people) {
      throw new Error('Wrong customer state provided.');
    }

    const people = newState?.application?.people;
    const customerIndex = people?.findIndex(
      (person) => person.uid === customerUID,
    );

    if (customerIndex === undefined || customerIndex < 0) {
      throw new Error('Did not find the customer in state by his UID.');
    }
    const customer = people[customerIndex];
    const customerPhones = customer.phones || [];

    const existingPhoneNumber = customerPhones?.find(
      (phone) => phone.number === phoneNumber,
    );

    if (existingPhoneNumber) {
      return { customerPhone: existingPhoneNumber, newState };
    }

    const newPhoneObject: CustomerPhone = {
      number: phoneNumber,
      type: 'secondary',
      uid: uuidv4(),
    };

    const newPhones = [...customerPhones, newPhoneObject];
    set(newState, `application.people[${customerIndex}].phones`, newPhones);

    return { customerPhone: newPhoneObject, newState };
  };

  const sendSMSWithVerificationLink = async (
    resolution_method: TIDCheckResolutionMethod,
    stateKey: string | null,
    customerUID: string | undefined,
    phoneNumberUID: string | undefined,
  ) => {
    try {
      if (resolution_method === 'idverse_check') {
        await fetchPostIdCheck({
          stateKey: stateKey!,
          body: {
            data: {
              offer_id: currentOfferId!,
              person_uid: customerUID!,
              phone_uid: phoneNumberUID!,
              redirect_url: window.location.href,
            },
          },
        });
      } else {
        await fetchPostOnfidoStart({
          stateKey: stateKey!,
          body: {
            data: { person_uid: customerUID!, phone_uid: phoneNumberUID! },
          },
        });
      }
      openModal();
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_) {
      onError();
    }
  };

  const handleSubmit = async ({ phoneNumber }: { phoneNumber: string }) => {
    const { data } = await refetchState();
    const { customerPhone, newState } = addPhoneNumberToStateObject(
      phoneNumber,
      applicantUID,
      data!.data,
    );

    await putState({ data: newState! });
    await sendSMSWithVerificationLink(
      resolution_method,
      stateKey,
      applicantUID,
      customerPhone?.uid,
    );
  };

  const methods = useForm({
    defaultValues: { phoneNumber: primaryPhone?.number || '' },
    resolver: yupResolver(phoneNumberValidator),
  });

  const onSubmit = methods.handleSubmit(handleSubmit);
  const { isSubmitting, isValidating } = methods.formState;

  return (
    <>
      <p className="mb-l font-reg">
        You'll need to do this on your phone. Just pop your phone number below,
        and we'll text you a link.
      </p>
      <FormProvider {...methods}>
        <form onSubmit={onSubmit} autoComplete="off" noValidate>
          <div className="mb-l flex w-[328px] flex-col">
            <HookFormInput
              name="phoneNumber"
              label="Mobile number"
              inputMode="tel"
              className="[&>div]:rounded-s"
            />
          </div>
          <div className="flex items-center justify-start">
            <WhiteLabelOverride componentType="button" disabled={isSubmitting}>
              <Button
                variant="primary"
                type="submit"
                loading={isSubmitting || isValidating}
              >
                {sendSmsButtonText}
              </Button>
            </WhiteLabelOverride>
            <div className="flex items-center pl-l text-xs font-med text-structure-40">
              Secured and powered by{' '}
              {resolution_method === 'idverse_check' ? (
                <img
                  src={idverse_logo}
                  alt="idverse logo"
                  className="ml-s h-[20px]"
                />
              ) : (
                <img
                  src={entrust_logo}
                  alt="Entrust logo"
                  className="ml-s h-[20px]"
                />
              )}
            </div>
          </div>
        </form>
      </FormProvider>
    </>
  );
};

function getCustomerPrimaryPhone(
  state: GetStateByStateKeyResponse['data'] | null,
  applicantUID: string | undefined,
) {
  if (!state || !applicantUID) {
    return;
  }

  const people = state?.application?.people?.filter(
    (person) => person.uid === applicantUID,
  );
  const customerPhones = people && people[0].phones;
  const primaryPhone = customerPhones?.filter(
    (phone) => phone.type === 'primary',
  )[0];

  return primaryPhone;
}
